import React, { useEffect, useState } from "react";

import {
  CHEQUE_NUMBER_LENGTH,
  getInorganicPaymentGatewayProviders,
  KH_VERTICAL_ID,
  LEARN_PAYMENT_MODE_OPTIONS,
  LEARN_VERTICAL_ID,
  MAX_BANK_TRANSFER_REFERENCE_ID_LENGTH,
  PAYMENT_MODE,
  PAYMENT_PLANS,
  PLUTUS_PAYMENT_LINK_SOURCE,
  RAZORPAY_PAYMENT_GATEWAY_PROVIDER,
  SCA_PAYMENT_MODE_OPTIONS,
  ONE_RUPEE,
} from "constants/config";
import {
  PUNCH_PAYMENT_V2_ERROR_MESSAGES,
  getApiErrorMessage,
} from "constants/apiErrorMessages";
import {
  DatePicker,
  PrimaryButton,
  SingleSelect,
  Text,
  Textfield,
  ErrorMessage,
} from "widgets";
import Loader from "Shared/Loader/Loader";
import dayjs from "dayjs";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-fox-toast";
import {
  useBankList,
  useCreatePaymentLink,
  usePunchPayment,
} from "store/backend/assets";

import styles from "./PaymentModes.module.scss";

const getPaymentModeOptions = (selectedOrderVerticalId) => {
  if (selectedOrderVerticalId === LEARN_VERTICAL_ID) {
    return LEARN_PAYMENT_MODE_OPTIONS.filter((paymentModeOption) =>
      [PAYMENT_MODE.ONLINE, PAYMENT_MODE.BANK_TRANSFER].includes(
        paymentModeOption.value,
      ),
    );
  }

  return SCA_PAYMENT_MODE_OPTIONS.filter(
    (paymentModeOption) =>
      paymentModeOption.value !== PAYMENT_MODE.BANK_TRANSFER,
  );
};

const buildPayloadForCashPayment = (orderId, amount, createdBy) => ({
  order_id: orderId,
  amount: amount,
  payment_mode_details: {
    created_by: createdBy,
    payment_mode: "CASH",
  },
});

const buildPayloadForChequePayment = (
  orderId,
  amount,
  chequeDetails,
  createdBy,
) => ({
  order_id: orderId,
  amount: amount,
  payment_mode_details: {
    cheque_number: chequeDetails.chequeNumber,
    bank_id: chequeDetails.bankId,
    created_by: createdBy,
    pdc_date: chequeDetails.pdcDate,
    payment_mode: "CHEQUE",
  },
});

const buildPayloadForBankTransferPayment = (
  orderId,
  amount,
  bankTransferDetails,
  createdBy,
  referenceId,
) => ({
  order_id: orderId,
  amount: amount,
  payment_mode_details: {
    reference_transaction_id: referenceId,
    bank_id: bankTransferDetails.bankId,
    created_by: createdBy,
    payment_mode: "BANK_TRANSFER",
  },
});

const buildPayloadForOnlinePayment = (orderId, amount, providerId) => ({
  order_id: orderId,
  amount: amount,
  payment_mode_details: {
    provider_id: providerId,
    payment_mode: "ONLINE",
  },
});
const handleOnClickPunchPaymentButton = (
  orderId,
  amountToBePaid,
  createdBy,
  paymentMode,
  chequeDetails,
  bankTransferDetails,
  referenceId,
  punchPayment,
  openSnackbar,
  setIsLinkCopied,
  setPaymentLink,
  createPaymentLink,
  paymentTypeId,
  paymentFlowTypeId,
  paymentGatewayProvider,
  currencyId,
) => {
  if (paymentMode === PAYMENT_MODE.ONLINE) {
    handleCreatePaymentLink(
      orderId,
      amountToBePaid,
      setPaymentLink,
      setIsLinkCopied,
      createPaymentLink,
      paymentTypeId,
      paymentFlowTypeId,
      paymentGatewayProvider,
      currencyId,
    );
    return;
  }

  const payload =
    paymentMode === PAYMENT_MODE.CASH
      ? buildPayloadForCashPayment(orderId, amountToBePaid, createdBy)
      : paymentMode === PAYMENT_MODE.CHEQUE
        ? buildPayloadForChequePayment(
            orderId,
            amountToBePaid,
            chequeDetails,
            createdBy,
          )
        : paymentMode === PAYMENT_MODE.BANK_TRANSFER
          ? buildPayloadForBankTransferPayment(
              orderId,
              amountToBePaid,
              bankTransferDetails,
              createdBy,
              referenceId,
            )
          : paymentMode === PAYMENT_MODE.ONLINE
            ? buildPayloadForOnlinePayment(
                orderId,
                amountToBePaid,
                paymentGatewayProvider,
              )
            : null;

  if (!payload) {
    openSnackbar("Invalid payment mode selected", "error");
    return;
  }

  punchPayment(payload, {
    onSuccess: (punchPaymentResponse) => {
      const successMessage = punchPaymentResponse?.payment_id
        ? `Payment successful. Payment ID: ${punchPaymentResponse.payment_id}`
        : "Payment successful";
      openSnackbar(successMessage, "success");
      setTimeout(() => window.location.reload(), 1000);
    },
    onError: (punchPaymentError) => {
      const message = getApiErrorMessage(
        punchPaymentError,
        PUNCH_PAYMENT_V2_ERROR_MESSAGES,
      );
      openSnackbar(message, "error");
    },
  });
};
const shouldEnablePunchPaymentButton = (
  paymentMode,
  chequeNumber,
  bankId,
  pdcDate,
  bankReferenceId,
  amountToBePaid,
  firstPaymentCollectionAmount,
) => {
  if (
    firstPaymentCollectionAmount &&
    amountToBePaid < firstPaymentCollectionAmount
  ) {
    return true;
  }

  if (!paymentMode) {
    return true;
  }
  if (Number(amountToBePaid) < ONE_RUPEE) {
    return true;
  }

  if (paymentMode === PAYMENT_MODE.ONLINE) {
    return false;
  }

  if (paymentMode === PAYMENT_MODE.CASH) {
    return false;
  }

  if (paymentMode === PAYMENT_MODE.CHEQUE) {
    return (
      !chequeNumber ||
      chequeNumber.length !== CHEQUE_NUMBER_LENGTH ||
      !bankId ||
      !pdcDate
    );
  }

  if (paymentMode === PAYMENT_MODE.BANK_TRANSFER) {
    return !bankReferenceId;
  }

  return true;
};
const handleCreatePaymentLink = (
  orderId,
  amountToBePaid,
  setPaymentLink,
  setIsLinkCopied,
  createPaymentLink,
  paymentTypeId,
  paymentFlowTypeId,
  paymentGatewayProvider,
  currencyId,
) => {
  const payload = {
    order_id: orderId,
    payment_link_amount: amountToBePaid,
    currency_id: currencyId,
    payment_type_id: paymentTypeId,
    payment_link_source_id: PLUTUS_PAYMENT_LINK_SOURCE,
    payment_flow_type_id: paymentFlowTypeId,
    payment_provider_id: paymentGatewayProvider,
  };

  createPaymentLink(payload, {
    onSuccess: (createdPaymentLinkUrl) => {
      setIsLinkCopied(false);
      setPaymentLink(createdPaymentLinkUrl);
    },
  });
};
const handleCopyLink = (paymentLink, setIsLinkCopied, setPaymentLink) => {
  navigator.clipboard.writeText(paymentLink);
  setIsLinkCopied(true);
  setPaymentLink("");
  toast.success("Copied Payment Link!");
};
const handleChangePaymentMode = (
  changeEvent,
  setPaymentLink,
  setPaymentMode,
  resetCreateLinkStatus,
) => {
  setPaymentMode(changeEvent.target.value);
  setPaymentLink("");
  resetCreateLinkStatus();
};
const handlePaymentGatewayProvider = (
  setPaymentGatewayProvider,
  changeEvent,
  setPaymentLink,
  resetCreateLinkStatus,
) => {
  setPaymentGatewayProvider(changeEvent.target.value);
  setPaymentLink("");
  resetCreateLinkStatus();
};
const PaymentModes = (props) => {
  const {
    orderId,
    amountToBePaid,
    createdBy,
    paymentLink,
    setPaymentLink,
    paymentTypeId,
    paymentFlowTypeId,
    firstPaymentCollectionAmount,
    selectedOrderVerticalId,
    currencyId,
    countryId,
    submitButtonLabel,
    submitButtonFirst,
  } = props;
  const { openSnackbar } = useSnackbarContext();
  const [chequeNumber, setChequeNumber] = useState("");
  const [bankId, setBankId] = useState(null);
  const [pdcDate, setPdcDate] = useState("");
  const [bankReferenceId, setBankReferenceId] = useState("");
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const paymentGatewayProviders = getInorganicPaymentGatewayProviders(
    selectedOrderVerticalId,
    countryId,
  ).map((provider) => ({
    key: provider.id,
    value: provider.value,
    label: provider.label || provider.name,
  }));

  const defaultProviderId =
    paymentGatewayProviders[0]?.value ??
    RAZORPAY_PAYMENT_GATEWAY_PROVIDER.value;

  const [paymentGatewayProvider, setPaymentGatewayProvider] = useState(
    defaultProviderId,
  );
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODE.ONLINE);
  const { data: bankList, isPending: isBankListLoading } = useBankList();
  const { mutate: punchPayment, isPending: isPunchPaymentLoading } =
    usePunchPayment();
  const {
    mutate: createPaymentLink,
    isPending: isCreatePaymentLinkLoading,
    isError: isErrorOnCreatePaymentLink,
    error: errorOnCreatePaymentLink,
    reset: resetCreateLinkStatus,
  } = useCreatePaymentLink();
  const bankMenuItems = (bankList ?? []).map((bank) => ({
    key: bank.id,
    value: bank.id,
    label: bank.name,
  }));
  const minDate = dayjs().subtract(21, "day");
  const maxDate = dayjs().add(1, "year");

  useEffect(() => {
    setPaymentGatewayProvider(defaultProviderId);
    setPaymentLink("");
    resetCreateLinkStatus();
  }, [orderId, defaultProviderId]);

  useEffect(() => {
    if (isErrorOnCreatePaymentLink) {
      resetCreateLinkStatus();
    }
    setPaymentLink("");
  }, [amountToBePaid]);

  const submitButtonBlock = (
    <div className={styles.submitButton}>
      <PrimaryButton
        onClick={() =>
          handleOnClickPunchPaymentButton(
            orderId,
            amountToBePaid,
            createdBy,
            paymentMode,
            { chequeNumber, bankId, pdcDate },
            { bankId },
            bankReferenceId,
            punchPayment,
            openSnackbar,
            setIsLinkCopied,
            setPaymentLink,
            createPaymentLink,
            paymentTypeId,
            paymentFlowTypeId,
            paymentGatewayProvider,
            currencyId
          )
        }
        disabled={shouldEnablePunchPaymentButton(
          paymentMode,
          chequeNumber,
          bankId,
          pdcDate,
          bankReferenceId,
          amountToBePaid,
          firstPaymentCollectionAmount,
        )}
      >
        {isPunchPaymentLoading || isCreatePaymentLinkLoading ? (
          <Loader />
        ) : paymentMode === PAYMENT_MODE.ONLINE ? (
          submitButtonLabel || "Create Payment Link"
        ) : (
          "Punch Payment"
        )}{" "}
      </PrimaryButton>
    </div>
  );

  return (
    <div className={styles.mainContainer}>
      {submitButtonFirst && submitButtonBlock}
      <div className={styles.field}>
        <Text>Select Payment Mode</Text>
        <SingleSelect
          value={paymentMode}
          menuItems={getPaymentModeOptions(selectedOrderVerticalId)}
          handleChange={(event) =>
            handleChangePaymentMode(
              event,
              setPaymentLink,
              setPaymentMode,
              resetCreateLinkStatus,
            )
          }
          tag="Select Payment Mode"
        />
      </div>
      {paymentMode === PAYMENT_MODE.ONLINE && (
        <div className={styles.field}>
          <Text>Select Payment Provider</Text>
          <SingleSelect
            value={
              selectedOrderVerticalId === KH_VERTICAL_ID
                ? RAZORPAY_PAYMENT_GATEWAY_PROVIDER.value
                : paymentGatewayProvider
            }
            menuItems={paymentGatewayProviders}
            handleChange={(event) =>
              handlePaymentGatewayProvider(
                setPaymentGatewayProvider,
                event,
                setPaymentLink,
                resetCreateLinkStatus,
              )
            }
            disabled={
              selectedOrderVerticalId === KH_VERTICAL_ID ||
              paymentGatewayProviders.length <= 1
            }
          />
        </div>
      )}
      {paymentMode === PAYMENT_MODE.CHEQUE && (
        <div className={styles.chequePaymentContainer}>
          <div className={styles.field}>
            <Text>Please enter cheque number</Text>
            <Textfield
              value={chequeNumber}
              handleOnChange={(event) => {
                const onlyNumbers = event.target.value
                  .replace(/\D/g, "")
                  .slice(0, CHEQUE_NUMBER_LENGTH);
                setChequeNumber(onlyNumbers);
              }}
              placeholder="Enter Cheque Number"
            />
          </div>
          <div className={styles.flexContainer}>
            {isBankListLoading ? (
              <Loader />
            ) : (
              <div className={styles.field}>
                <Text>Please select bank</Text>
                <SingleSelect
                  value={bankId}
                  menuItems={bankMenuItems}
                  handleChange={(event) => setBankId(event.target.value)}
                  placeholder="Select Bank"
                  tag="Bank"
                />
              </div>
            )}
            <div className={styles.field}>
              <Text>Please select cheque date</Text>
              <DatePicker
                value={pdcDate}
                onChange={(selectedPdcDate) => setPdcDate(selectedPdcDate)}
                placeholder="Select Cheque Date"
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
          </div>
        </div>
      )}
      {paymentMode === PAYMENT_MODE.BANK_TRANSFER && (
        <div className={styles.bankTransferContainer}>
          <div className={styles.field}>
            <Text>Please enter reference ID</Text>
            <Textfield
              value={bankReferenceId}
              handleOnChange={(event) => {
                const validInput = event.target.value
                  .replace(/\s+/g, "")
                  .slice(0, MAX_BANK_TRANSFER_REFERENCE_ID_LENGTH);
                setBankReferenceId(validInput);
              }}
              placeholder="Enter Reference ID"
            />
          </div>
          <div className={styles.flexContainer}>
            {isBankListLoading ? (
              <Loader />
            ) : (
              <div className={styles.field}>
                <Text>Please select bank</Text>
                <SingleSelect
                  value={bankId}
                  menuItems={bankMenuItems}
                  handleChange={(event) => setBankId(event.target.value)}
                  placeholder="Select Bank"
                  tag="Bank"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {!submitButtonFirst && submitButtonBlock}
      {paymentLink && (
        <div
          className={styles.paymentLinkContainer}
          onClick={() =>
            handleCopyLink(paymentLink, setIsLinkCopied, setPaymentLink)
          }
        >
          <div className={styles.copyLinkText}>Copy Link</div>
          <ContentCopyIcon className={styles.copyIcon} />
        </div>
      )}
      {isErrorOnCreatePaymentLink && (
        <ErrorMessage>
          {(() => {
            const friendlyMessage = getApiErrorMessage(
              errorOnCreatePaymentLink,
              PUNCH_PAYMENT_V2_ERROR_MESSAGES,
            );
            const isLoanAlreadyExistsMessage =
              friendlyMessage ===
              PUNCH_PAYMENT_V2_ERROR_MESSAGES.ACTIVE_LOAN_ALREADY_EXISTS;
            if (
              isLoanAlreadyExistsMessage &&
              paymentFlowTypeId !== PAYMENT_PLANS.LOAN
            ) {
              return "Failed to create payment link. Please try again.";
            }
            return friendlyMessage;
          })()}
        </ErrorMessage>
      )}
    </div>
  );
};

export default PaymentModes;
